// Employee.tsx (shadcn/ui version)
import React, { useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ru";

import { EmployeeI } from "./index";
import { toast } from "react-toastify";

import { MoreHorizontal, Trash2, Shield, User as UserIcon, Crown } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface EmployeeProps {
    employee: EmployeeI;
    onRoleChange: ({ uid, role }: { uid: string; role: string }) => Promise<void> | void;
    onDelete: (employeeId: string) => Promise<void> | void;
}

function getInitials(name?: string | null) {
    const n = (name ?? "").trim();
    if (!n) return "U";
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase()).join("") || "U";
}

export default function Employee({ employee, onRoleChange, onDelete }: EmployeeProps) {
    const { photoURL, displayName, email, gender, birthDate, age, uid, role } = employee;

    const formattedBirthDate = useMemo(() => {
        return birthDate ? dayjs(birthDate).locale("ru").format("DD.MM.YYYY") : "Не указано";
    }, [birthDate]);

    const roleLabel =
        role === "superadmin" ? "Супер админ" : role === "admin" ? "Админ" : "Сотрудник";

    const roleMeta = useMemo(() => {
        if (role === "superadmin") {
            return {
                badge: (
                    <Badge variant="default" className="gap-1">
                        <Crown className="h-3.5 w-3.5" />
                        Superadmin
                    </Badge>
                ),
            };
        }
        if (role === "admin") {
            return {
                badge: (
                    <Badge variant="secondary" className="gap-1">
                        <Shield className="h-3.5 w-3.5" />
                        Админ
                    </Badge>
                ),
            };
        }
        return {
            badge: (
                <Badge variant="outline" className="gap-1">
                    <UserIcon className="h-3.5 w-3.5" />
                    Сотрудник
                </Badge>
            ),
        };
    }, [role]);

    const handleChangeRole = async (newRole: string) => {
        try {
            await onRoleChange({ uid, role: newRole });
            toast.success(`Роль пользователя ${displayName || ""} изменена на ${newRole}`);
        } catch (e) {
            console.error(e);
            toast.error("Не удалось изменить роль");
        }
    };

    const handleDeleteUser = async () => {
        try {
            await onDelete(uid);
            toast.success(`Пользователь ${displayName || ""} удален`);
        } catch (e) {
            console.error(e);
            toast.error("Не удалось удалить пользователя");
        }
    };

    // toggle user <-> admin (как раньше)
    const toggleRole = role === "user" ? "admin" : role === "admin" ? "user" : "";
    const toggleRoleText = role === "user" ? "Сделать администратором" : role === "admin" ? "Сделать пользователем" : "";

    return (
        <Card className="p-4">
            {/* Desktop */}
            <div className="hidden md:grid grid-cols-[56px_2fr_1fr_1fr_1fr_1fr_40px] items-center gap-4">
                <div className="flex justify-center">
                    <Avatar className="h-10 w-10">
                        <AvatarImage src={photoURL ?? ""} alt={displayName ?? "Аватар"} />
                        <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
                    </Avatar>
                </div>

                <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <p className="font-medium truncate">{displayName || "Без имени"}</p>
                        {roleMeta.badge}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{email || "Не указано"}</p>
                </div>

                <InfoCell label="Пол" value={gender || "Не указано"} />
                <InfoCell label="День рождения" value={formattedBirthDate} />
                <InfoCell label="Возраст" value={age ? String(age) : "Не указано"} />
                <InfoCell label="Должность" value={roleLabel} />

                <div className="flex justify-end">
                    <EmployeeActions
                        role={role}
                        toggleRole={toggleRole}
                        toggleRoleText={toggleRoleText}
                        onChangeRole={handleChangeRole}
                        onDelete={handleDeleteUser}
                    />
                </div>
            </div>

            {/* Mobile */}
            <div className="md:hidden space-y-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="h-10 w-10 shrink-0">
                            <AvatarImage src={photoURL ?? ""} alt={displayName ?? "Аватар"} />
                            <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
                        </Avatar>

                        <div className="min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                                <p className="font-medium truncate">{displayName || "Без имени"}</p>
                                {roleMeta.badge}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{email || "Не указано"}</p>
                        </div>
                    </div>

                    <EmployeeActions
                        role={role}
                        toggleRole={toggleRole}
                        toggleRoleText={toggleRoleText}
                        onChangeRole={handleChangeRole}
                        onDelete={handleDeleteUser}
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <InfoCell label="Пол" value={gender || "Не указано"} />
                    <InfoCell label="День рождения" value={formattedBirthDate} />
                    <InfoCell label="Возраст" value={age ? String(age) : "Не указано"} />
                    <InfoCell label="Должность" value={roleLabel} />
                </div>
            </div>
        </Card>
    );
}

function InfoCell({ label, value }: { label: string; value: string }) {
    return (
        <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="truncate">{value}</p>
        </div>
    );
}

function EmployeeActions({
                             role,
                             toggleRole,
                             toggleRoleText,
                             onChangeRole,
                             onDelete,
                         }: {
    role: string;
    toggleRole: string;
    toggleRoleText: string;
    onChangeRole: (role: string) => void;
    onDelete: () => void;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg">
                    <MoreHorizontal className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Действия</DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* user <-> admin */}
                {toggleRole ? (
                    <DropdownMenuItem onClick={() => onChangeRole(toggleRole)}>
                        <Shield className="mr-2 h-4 w-4" />
                        {toggleRoleText}
                    </DropdownMenuItem>
                ) : null}

                {/* ДОБАВИЛИ: сделать superadmin */}
                {role !== "superadmin" ? (
                    <DropdownMenuItem onClick={() => onChangeRole("superadmin")}>
                        <Crown className="mr-2 h-4 w-4" />
                        Сделать супер админом
                    </DropdownMenuItem>
                ) : null}

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Удалить
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
